'use client'

/**
 * Client-side product analytics (T-AN-01; D-20).
 *
 * Two rules this module exists to enforce:
 *
 * 1. **The event list is closed.** `EVENTS` is the product's event names and
 *    their property shapes; `capture` only accepts those, so a typo becomes
 *    a type error rather than an event nobody ever sees in PostHog.
 * 2. **No event is sent from both sides.** Mutations are captured
 *    server-side (`api/src/uniap/analytics.py`); the three listed here are
 *    the client's half. Sending one from both would double every count.
 *
 * PostHog is loaded lazily, after hydration and after the browser reports
 * itself idle, because the dashboard has a 2s TTI budget on throttled 3G
 * (FR-8, T-PERF-01) and analytics is never worth part of it. Do Not Track is
 * honoured by simply never loading the library.
 */

/** Which search surface a result was listed on. */
export type SearchSurface = 'match' | 'browse' | 'universities'

export interface EventMap {
  fork_choice: {choice: 'decided' | 'discover'}
  wizard_step_completed: {step: number; skipped: boolean}
  quiz_started: Record<string, never>
  /** A result's title was followed, from any search tab (MEAS-10). */
  /**
   * `to_catalogue`: the click opens a catalogue page. Always true for a
   * catalogue row; for a live result, true when its host resolved to a
   * registry row (D-32) — the Phase 1 exit metric.
   */
  result_clicked: {mode: SearchSurface; live: boolean; position: number; to_catalogue: boolean}
  /** A link that leaves Uniap for the institution's or funder's own page. */
  outbound_clicked: {surface: 'card' | 'detail'; live: boolean}
  /** An unanswered requirement sent the student to fill in their profile. */
  profile_gap_clicked: {key: string}
}

/** One event and its properties, as a serialisable value for a client island. */
export type TrackedEvent = {
  [K in keyof EventMap]: {name: K; properties: EventMap[K]}
}[keyof EventMap]

/**
 * Captured by the API, never from here. Listed so the split is auditable.
 *
 * `search_performed` is the one view among them (D-20 amendment): only the
 * API knows the result count, the live count and the quota state.
 */
export const SERVER_EVENTS = [
  'signup',
  'quiz_completed',
  'majors_confirmed',
  'search_performed',
  'item_saved',
  'item_status_changed',
  'search_saved',
  'saved_search_opened',
  'saved_search_removed'
] as const

export const CLIENT_EVENTS = [
  'fork_choice',
  'wizard_step_completed',
  'quiz_started',
  'result_clicked',
  'outbound_clicked',
  'profile_gap_clicked'
] as const

/** Every event this product sends. A unit test asserts the list. */
export const ALL_EVENTS = [...CLIENT_EVENTS, ...SERVER_EVENTS] as const

/** URL properties PostHog fills in from `location` and `document.referrer`. */
const URL_PROPERTIES = [
  '$current_url',
  '$referrer',
  '$initial_current_url',
  '$initial_referrer'
] as const

/**
 * Drop a URL's query string and fragment, keeping only `utm_*` parameters.
 *
 * Search pages carry what a student typed in the query string (§8 forbids
 * sending filter values), so no URL leaves the browser with its query intact.
 * Campaign tags are the one exception: they are ours, not the student's, and
 * Web Analytics needs them for its channel breakdown.
 */
export function stripUrl(href: string): string {
  let url: URL
  try {
    url = new URL(href)
  } catch {
    return href
  }
  const kept = new URLSearchParams()
  url.searchParams.forEach((value, key) => {
    if (key.startsWith('utm_')) kept.append(key, value)
  })
  const query = kept.toString()
  return `${url.origin}${url.pathname}${query ? `?${query}` : ''}`
}

type CaptureResult = {
  properties?: Record<string, unknown>
  $set?: Record<string, unknown>
  $set_once?: Record<string, unknown>
}

/** `before_send` hook: strip every URL property on every event, page leaves included. */
export function stripEventUrls<T extends CaptureResult | null>(event: T): T {
  if (!event) return event
  for (const bag of [event.properties, event.$set, event.$set_once]) {
    if (!bag) continue
    for (const key of URL_PROPERTIES) {
      if (typeof bag[key] === 'string') bag[key] = stripUrl(bag[key] as string)
    }
  }
  return event
}

type PostHogLike = {
  init: (key: string, options: Record<string, unknown>) => void
  register: (properties: Record<string, unknown>) => void
  capture: (event: string, properties?: Record<string, unknown>) => void
  identify: (id: string, properties?: Record<string, unknown>) => void
  reset: () => void
}

let client: PostHogLike | null = null
let loading: Promise<PostHogLike | null> | null = null

/** Resolve the same-origin PostHog proxy unless an explicit host is supplied. */
export function posthogApiHost(origin: string, configured?: string): string {
  return configured?.trim() || `${origin}/x9k`
}

function analyticsDisabled(): boolean {
  if (typeof window === 'undefined') return true
  if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) return true
  // D-20: respect the browser's Do Not Track signal.
  const legacy = (window as unknown as {doNotTrack?: string}).doNotTrack
  return navigator.doNotTrack === '1' || legacy === '1'
}

async function load(): Promise<PostHogLike | null> {
  if (client) return client
  if (analyticsDisabled()) return null
  if (!loading) {
    loading = import('posthog-js').then((module) => {
      const posthog = module.default as unknown as PostHogLike
      posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
        api_host: posthogApiHost(window.location.origin, process.env.NEXT_PUBLIC_POSTHOG_HOST),
        // Page views are recorded by `identify` + explicit events; automatic
        // capture would also record form values and click targets, which is
        // more personal data than this product needs (§8).
        autocapture: false,
        // Page views are sent by `PageviewTracker` on every route change
        // (`capturePageview`), not by the library's history patching, so
        // the URL can be stripped first. Page leaves give Web Analytics its
        // bounce rate and session duration.
        capture_pageview: false,
        capture_pageleave: true,
        persistence: 'localStorage',
        before_send: stripEventUrls
      })
      // One PostHog project serves production only; this tags anything that
      // slips through from elsewhere so it can be filtered out.
      posthog.register({environment: process.env.NEXT_PUBLIC_UNIAP_ENV ?? 'development'})
      client = posthog
      return posthog
    })
  }
  return loading
}

/** Wait for an idle moment; fall back to a short timeout where unsupported. */
function whenIdle(run: () => void) {
  if (typeof window === 'undefined') return
  const idle = (window as unknown as {requestIdleCallback?: (cb: () => void) => void})
    .requestIdleCallback
  if (idle) idle(run)
  else setTimeout(run, 1200)
}

export function capture<K extends keyof EventMap>(event: K, properties: EventMap[K]): void {
  whenIdle(() => {
    void load().then((posthog) => posthog?.capture(event, properties))
  })
}

/** A page view, for Web Analytics. Called by `PageviewTracker` on each route change. */
export function capturePageview(): void {
  whenIdle(() => {
    void load().then((posthog) =>
      posthog?.capture('$pageview', {$current_url: stripUrl(window.location.href)})
    )
  })
}

export function identify(userId: string, properties: {locale: string}): void {
  whenIdle(() => {
    // D-20: the distinct id is the opaque user id, never an email.
    void load().then((posthog) => posthog?.identify(userId, properties))
  })
}

export function resetIdentity(): void {
  void load().then((posthog) => posthog?.reset())
}
